import { Box, Typography } from '@mui/material'
import Button from 'components/buttons/Button'
import Popup from 'components/popup/Popup'
import PopupSection from 'components/popup/PopupSection'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { userLang } from 'utils/lang'
import { extractCountryCode } from 'utils/phone'

interface AddMemberByPhonePopupProps {
  children: ReactNode
  open: boolean
  setOpen: (open: boolean) => void
  onAddMember: (phone: string) => void
}

function AddMemberByPhonePopupContent(props: {
  onAddMember: (phone: string) => void
  setOpen: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [phone, setPhone] = useState('')
  const [country] = useState<string | undefined>(userLang())
  const phoneInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (phone.length === 0)
      setTimeout(() => {
        phoneInputRef.current?.focus()
      }, 100)
  }, [phone])

  const handleAdd = () => {
    if (isValidPhoneNumber(`+${phone}`)) {
      props.onAddMember(`+${phone}`)

      // keep previous phone number country code for next add
      const countryCode = extractCountryCode(`+${phone}`)
      setPhone(countryCode.slice(1)) // Remove + as PhoneInput handles it

      setTimeout(() => {
        phoneInputRef.current?.focus()
      }, 100)
    }
  }

  // on enter click, add member in case phone number is valid
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleAdd()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [phone])

  return (
    <Box paddingTop={'0.5rem'} paddingBottom={'0.5rem'}>
      <PopupSection noBottomBorder title={t('addMembersByPhone')}>
        <Box
          sx={{
            width: 300,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              direction: 'ltr',
              display: 'flex',
              justifyContent: 'center',
              mt: '1rem',
              mb: '0.1rem',
              '& .react-tel-input .selected-flag:hover, & .react-tel-input .selected-flag.open':
                {
                  backgroundColor: 'transparent'
                }
            }}
          >
            <PhoneInput
              enableSearch
              placeholder={t('phoneNumberPlaceholder', 'Phone number')}
              inputProps={{ ref: phoneInputRef }}
              inputStyle={{
                width: '100%',
                height: '40px',
                borderRadius: '20px',
                border: '1px solid #B9B9B9'
              }}
              buttonStyle={{
                borderTopLeftRadius: '20px',
                borderBottomLeftRadius: '20px',
                border: '1px solid #B9B9B9',
                borderRight: 'none',
                backgroundColor: 'transparent'
              }}
              containerStyle={{
                width: '100%'
              }}
              dropdownStyle={{
                width: '300px'
              }}
              country={country}
              value={phone}
              onChange={(value) => setPhone(value)}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '20px',
              paddingBottom: '10px'
            }}
          >
            <Button
              size="xs"
              width="fit-content"
              onClick={handleAdd}
              disabled={!isValidPhoneNumber(`+${phone}`)}
            >
              {t('addMember', 'Add member')}
            </Button>
          </Box>
        </Box>
      </PopupSection>
    </Box>
  )
}

export default function AddMemberByPhonePopup(
  props: AddMemberByPhonePopupProps
) {
  return (
    <Popup
      padding={0}
      closeOnTouchOutside
      open={props.open}
      setOpen={props.setOpen}
      placement="left"
      content={
        <AddMemberByPhonePopupContent
          onAddMember={props.onAddMember}
          setOpen={props.setOpen}
        />
      }
    >
      {props.children}
    </Popup>
  )
}
